import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { SortableList } from '../../../components/SortableList/SortableList';
import { RendererType } from '../../../data-models/field-definition';
import { TierConfigViewModel } from '../../../schemas/FundWaterfall.schema';
import { cardStyles } from '../../../theme/component-styles';
import { createFormField } from '../../../view-models/form.view-model';
import { WaterfallTierConfigCardCss } from './WaterfallConfig.styles';
import { useWaterfallTiersConfig } from './useWaterfallTiersConfig';

export type WaterfallTiersConfigProps = {
  tiers: TierConfigViewModel[];
  onSave: (tiers: TierConfigViewModel[]) => void | Promise<void>;
};
export function WaterfallTiersConfig(props: WaterfallTiersConfigProps) {
  const { onRename, onDelete, onAdd, onReorder, formTierData } = useWaterfallTiersConfig(props);
  return (
    <Box style={{ ...cardStyles, marginBlock: '1rem' }}>
      <Typography variant='body1' margin='1rem 0 0.25rem 0'>
        Waterfall Phases
      </Typography>
      <Typography
        mb='0.5rem'
        variant='body2'
        display='flex'
        alignItems='end'
        gap='0.2rem'
        color='text.secondary'
      >
        <InfoOutlinedIcon />
        Manage tiers: add, remove, rename, reorder
      </Typography>
      <Stack gap='1rem' mb='1rem'>
        <SortableList
          items={formTierData}
          onChange={onReorder}
          renderItem={(item) => (
            <SortableItem id={item.id}>
              <EditableTierItem
                tier={item as TierConfigViewModel}
                onDelete={onDelete}
                onRename={onRename}
                key={`${item.id}-${item.orderNumber}`}
              />
            </SortableItem>
          )}
        />
        <Button
          startIcon={<AddIcon />}
          color='secondary'
          onClick={onAdd}
          sx={{ alignSelf: 'start' }}
          size='medium'
        >
          Add Tier
        </Button>
      </Stack>
    </Box>
  );
}

export function EditableTierItem({
  disableDrag,
  onDelete,
  onRename,
  tier,
}: {
  disableDrag?: boolean;
  onDelete: (tier: TierConfigViewModel) => void | Promise<void>;
  onRename: (tier: TierConfigViewModel) => void | Promise<void>;
  tier: TierConfigViewModel;
}) {
  const [name, setName] = useState(tier.tierName);
  const _onRename = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setName(newName);
      // TODO: test if re-renders are a problem
      // might have to call onRename on blur, + here here with a debounce
      // as re-renders will cause the input to lose focus
      // or maybe change the whole approach and do a fire and forget unless errors are returned
      onRename({ ...tier, tierName: newName });
    },
    [onRename, tier]
  );
  const nameField = createFormField({
    key: 'tierName',
    renderer: RendererType.text,
    variant: 'form-inline',
    placeholder: 'Tier Name',
  });
  const formProps = useMemo(
    () =>
      ({
        value: name,
        onChange: _onRename,
      }) as ControllerRenderProps,
    [name, _onRename]
  );
  return (
    <>
      <Typography component='div' variant='body2' style={cardStyles} css={WaterfallTierConfigCardCss}>
        <BasicDragHandle disabled={disableDrag} />
        <FieldFactory formField={nameField} formProps={formProps} />
        <Button color='secondary' onClick={() => onDelete(tier)}>
          Remove
        </Button>
      </Typography>
    </>
  );
}
